export type LogLevel = 'debug' | 'info' | 'warn' | 'error';
export type LogFormat = 'pretty' | 'json';

export interface Logger {
  debug(message: string, context?: Record<string, unknown>): void;
  info(message: string, context?: Record<string, unknown>): void;
  warn(message: string, context?: Record<string, unknown>): void;
  error(message: string, context?: Record<string, unknown>): void;
}

const LEVEL_ORDER: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
};

function formatContext(context: Record<string, unknown>): string {
  return Object.entries(context)
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' ');
}

function formatPretty(
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
): string {
  const timestamp = new Date().toISOString();
  const tag = level.toUpperCase().padEnd(5);
  const base = `[${timestamp}] ${tag} ${message}`;
  if (context && Object.keys(context).length > 0) {
    return `${base} ${formatContext(context)}`;
  }
  return base;
}

function formatJson(
  level: LogLevel,
  message: string,
  context?: Record<string, unknown>,
): string {
  const entry: Record<string, unknown> = {
    timestamp: new Date().toISOString(),
    level,
    message,
    ...context,
  };
  return JSON.stringify(entry);
}

export function createLogger(options: {
  level: LogLevel;
  format: LogFormat;
}): Logger {
  const threshold = LEVEL_ORDER[options.level];
  const format = options.format === 'json' ? formatJson : formatPretty;

  function emit(
    level: LogLevel,
    message: string,
    context?: Record<string, unknown>,
  ): void {
    if (LEVEL_ORDER[level] < threshold) return;

    const line = format(level, message, context);

    if (level === 'error') {
      console.error(line);
    } else if (level === 'warn') {
      console.warn(line);
    } else {
      console.log(line);
    }
  }

  return {
    debug: (message, context?) => emit('debug', message, context),
    info: (message, context?) => emit('info', message, context),
    warn: (message, context?) => emit('warn', message, context),
    error: (message, context?) => emit('error', message, context),
  };
}

let globalLogger: Logger = createLogger({ level: 'info', format: 'pretty' });

export function setGlobalLogger(logger: Logger): void {
  globalLogger = logger;
}

export function getLogger(): Logger {
  return globalLogger;
}
